<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { PhxIcon, PhxWatchComparatorToaster } from '@phx-styleguides/jlc';
import { emitter } from '@phoenix/event-bus';
import {
  WATCH_COMPARATOR_TOGGLED,
  WATCH_COMPARATOR_UPDATED,
} from '@maison/helpers/watch-comparator-localstorage-helper';
import { useWatchComparator } from '@maison/composables/watchComparator';

const toasterRef = ref(null);
const { watches, count, remove, MAX_WATCHES } = useWatchComparator();

const placeholders = computed(() => Math.max(0, MAX_WATCHES - count.value));

const onUpdate = (payload) => {
  if (payload?.added) {
    toasterRef.value?.notifyWatches();
  } else if (count.value === 0) {
    toasterRef.value?.clear();
  }
};

const onToggle = (active) => {
  if (!active) {
    toasterRef.value?.clear();
    return;
  }

  if (count.value > 0) {
    toasterRef.value?.notifyWatches();
  } else {
    toasterRef.value?.notifyActivation();
  }
};

onMounted(() => {
  emitter.on(WATCH_COMPARATOR_UPDATED, onUpdate);
  emitter.on(WATCH_COMPARATOR_TOGGLED, onToggle);

  if (count.value > 0 && document.querySelector('.phoenix-watch-comparator-button')) {
    toasterRef.value?.notifyWatches();
  }
});

onBeforeUnmount(() => {
  emitter.off(WATCH_COMPARATOR_UPDATED, onUpdate);
  emitter.off(WATCH_COMPARATOR_TOGGLED, onToggle);
});
</script>

<template>
  <phx-watch-comparator-toaster ref="toasterRef">
    <template #toast="{ toast }">
      <template v-if="toast.variant === 'activation'">
        <div class="watch-comparator-toaster__toast-header" data-cy="watch-comparator-activation">
          <div class="uppercase font-secondary font-semibold">{{ $t('WatchComparator.toaster.activation.title') }}</div>

          <p>{{ $t('WatchComparator.toaster.activation.description', { max: MAX_WATCHES }) }}</p>
        </div>
      </template>

      <template v-else-if="toast.variant === 'watches'">
        <div class="watch-comparator-toaster__toast-header" data-cy="watch-comparator-recap">
          <div class="uppercase font-secondary font-semibold">{{ $t('WatchComparator.toaster.recap.title') }}</div>

          <p data-cy="watch-comparator-recap-count">
            {{ $t('WatchComparator.toaster.recap.description', { count: count, max: MAX_WATCHES }) }}
          </p>
        </div>

        <div class="watch-comparator-toaster__toast-body">
          <div class="watch-comparator-toaster__items" role="list">
            <div
              v-for="watch in watches"
              :key="watch.reference"
              class="watch-comparator-toaster__item"
              role="listitem"
              data-cy="watch-comparator-recap-item"
            >
              <img v-if="watch.image" :src="watch.image" :alt="watch.name" />

              <button
                type="button"
                class="btn btn--default btn--rounded btn--icon-only btn--icon btn--negative watch-comparator-toaster__remove-btn"
                :aria-label="$t('WatchComparator.toaster.recap.remove.ariaLabel')"
                data-cy="watch-comparator-recap-remove"
                @click="remove(watch.reference)"
              >
                <span class="btn__icon">
                  <phx-icon icon="cross-10" size="10" />
                </span>
              </button>
            </div>

            <div v-for="n in placeholders" :key="`placeholder-${n}`" class="watch-comparator-toaster__item">
              <phx-icon icon="watch-16" size="16" />
            </div>
          </div>

          <button
            type="button"
            class="btn btn--default btn--negative max-sm:btn--sm"
            data-panel-open="watch-comparator-panel"
            data-cy="watch-comparator-compare"
          >
            <span class="btn__text">{{ $t('WatchComparator.compare.label') }}</span>
          </button>
        </div>
      </template>

      <template v-else>
        {{ toast.text }}
      </template>
    </template>
  </phx-watch-comparator-toaster>
</template>
